import React from 'react';
import {
    Responsive,
    Datagrid,
    Filter,
    SimpleList,
    SimpleForm,
    TextField,
    ReferenceInput,
    TextInput,
    NumberInput,
    required
} from 'admin-on-rest';
import {DependentInput} from 'aor-dependent-input';
import List from '../admin/ListLocationInventory';
import Edit from '../admin/EditLocationInventory';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import DeleteLocationInventory from '../admin/DeleteLocationInventory';
import ProductByTypeNameIdBrandDependentSelectInput from '../components/ProductByTypeNameIdBrandDependentSelectInput';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {viewStyles} from '../shared/styles-view';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/action/assignment-turned-in';
import Icon2 from 'material-ui/svg-icons/action/assignment';

export const fuseLocationInventoryIcon = Icon;
export const fuseLocationInventoryLevelsIcon = Icon2;

/*
 * View: List (for Inventory Adjustments)
 */

// Create Search and Filter: Inventory for Location

const LocationInventoryFilter = (props) => (
    <Filter {...props}>
        <ReferenceInput
            label="Select a Location or type in a Location ID to view its Inventory."
            source="location_id_select"
            reference="LocationsForInventory"
            perPage={10000}
            allowEmpty
            alwaysOn
            style={viewStyles.filterLeft}>
            <VirtualizedSelectInput
                addField={true} optionText="filter_name" optionValue="id"
                options={{
                    className: 'full-width',
                    clearable: true,
                    placeholder: 'Select a Location or type in a Location ID to view its Inventory.'
                }}
            />
        </ReferenceInput>
    </Filter>
);

// Set title for List Location Inventory
const ListTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                className="avatar-title"/>View Inventory for Location</span>;

const ListDescription = () => {
    return (
        <span>
            <p>Select a Location or type in a Location ID to view its inventory.</p>
            <p>For Locations with Inventory items, you may adjust the quantity of each Product, or zero out the entire
                inventory for the currently selected Location.</p>
        </span>
    );
};

const ListDescriptionViewOnly = () => {
    return (
        <span>
            <p>Select a Location or type in a Location ID to view its inventory.</p>
            <p>For Locations with Inventory items, you may view the quantity of each Product.</p>
        </span>
    );
};

export const fuseLocationInventoryList = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescription/>} filters={<LocationInventoryFilter/>}
          requiresFilter={true} showButtons={true}
          perPage={100} sort={{field: 'product', order: 'ASC'}}>
        <Responsive
            small={
                <SimpleList
                    primaryText={record => record.product_name}
                    secondaryText={record => record.product}
                    tertiaryText={record => record.qty}
                />
            }
            medium={
                <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                    <TextField source="location" label="Location" sortable={false}/>
                    <TextField source="product" label="Product ID"/>
                    <TextField source="product_name" label="Product"/>
                    <TextField source="qty" label="Qty +/-"/>
                </Datagrid>
            }
        />
    </List>
);

export const fuseLocationInventoryListViewOnly = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescriptionViewOnly/>} filters={<LocationInventoryFilter/>}
          requiresFilter={true} showButtons={false}
          perPage={100} sort={{field: 'product', order: 'ASC'}}>
        <Responsive
            small={
                <SimpleList
                    primaryText={record => record.product_name}
                    secondaryText={record => record.product}
                    tertiaryText={record => record.qty}
                />
            }
            medium={
                <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                    <TextField source="location" label="Location" sortable={false}/>
                    <TextField source="product" label="Product ID"/>
                    <TextField source="product_name" label="Product"/>
                    <TextField source="qty" label="Qty +/-"/>
                </Datagrid>
            }
        />
    </List>
);

/*
 * View: Edit form (for Location Inventory Adjustment)
 */

const EditDescription = () => {
    return (
        <span>
            <p>Add an adjustment to a Location's inventory. This is not a total items of a Product available, but a
                positive or negative adjustment to that total.</p>
            <p>Examples:</p>
            <ul>
                <li>100 items of a specific Product exist at this Location, and 20 of them have been sold: add an
                    adjustment of <em>-20</em>.</li>
                <li>100 items of a specific Product exist at this Location, and 50 more have arrived: add an
                    adjustment of <em>50</em>.</li>
            </ul>
            <p>Select a Product Type, then a Product to adjust.</p>
        </span>
    );
};

export const fuseLocationInventoryEdit = (props) => (
    <Edit {...props} title="Add Adjustment for Location: " desc={<EditDescription/>}>
        <SimpleForm submitOnEnter={false} redirect="list">

            <ReferenceInput
                label="Select a Product Type*"
                source="product_type"
                reference="ProductTypes"
                perPage={10000}
                validate={[required]}
                allowEmpty>
                <VirtualizedSelectInput
                    addField={true}
                    optionText="name"
                    optionValue="name"
                    errorMessage="Product Type is required."
                    required={true}
                    options={{
                        clearable: false,
                        labelKey: 'name',
                        placeholder: 'Select a Product Type*',
                        required: true
                    }}
                />
            </ReferenceInput>
            <span style={viewStyles.fieldDescription}>
                Select a <strong>Product Type</strong>, then select a Product.
                <br/>
                (If Type is not available, please exit and go
                    to <a href="#/ProductTypes/create" style={viewStyles.fieldLink}>Add Product Type</a>).
                <TooltipIcon dataFor="ttType"/>
                <ReactTooltip id="ttType" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>Product Type</strong> is used for configuring the POS System to display products to be
                    sold dependent on your Location's DBA. In most cases, a business will only have one Product Type.
                </ReactTooltip>
            </span>

            <DependentInput dependsOn="product_type">
                <ProductByTypeNameIdBrandDependentSelectInput
                    {...props}
                    source="product"
                    dependentProp="dependsOnValue"
                    optionValue="id"
                    optionText="name"
                    label="Select a Product"
                    validate={[required]}
                    allowEmpty/>
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>Product</strong> to adjust inventory.
                    <br/>
                    (If Product is not available, please exit and go
                    to <a href="#/Products/create" style={viewStyles.fieldLink}>Add Product</a>).
                </span>
            </DependentInput>

            <DependentInput dependsOn="product">
                <TextInput source="product_name" style={viewStyles.hidden} elStyle={viewStyles.hidden}/>
            </DependentInput>

            <DependentInput dependsOn="product">
                <NumberInput source="piece_qty" label="Qty (+/-)"/>
                <span style={viewStyles.fieldDescription}>
                Increase or decrease the Product total.
                <TooltipIcon dataFor="ttQty"/>
                <ReactTooltip id="ttQty" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The Quantity reflects a change to the total of this Product at this Location. If there are 100
                    items of this Product and 20 have been sold, set the <strong>Qty</strong> to <em>-20</em>.
                </ReactTooltip>
            </span>
            </DependentInput>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Location Inventory List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Edit>
);

/*
 * View: List (for Zero Out Inventory)
 */

/*
 * View: Delete/Zero Out Inventory
 */

// Set title for Zero Out Inventory
const DeleteTitle = ({record}) => {
    return <span>Zero Out Inventory for Location {record ? `${record.id}` : ''}</span>;
};

export const fuseLocationInventoryZeroOut = (props) =>
    <DeleteLocationInventory {...props} title={<DeleteTitle/>} listPath="/LocationInventory"/>;



// WEBPACK FOOTER //
// ./src/Fuse/Inventory/locationInventory.js